import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Platform } from 'ionic-angular';
import { Clipboard } from '@ionic-native/clipboard';
import { LoggerEntry, LoggerProvider } from '../../providers/logger/logger';
import { DebugLogViewMapper, IDebugLogItem } from '../../providers/logger/debug-log-view';
import { DEBUG_LOG_CATEGORIES, DebugLogCategory } from '../../model/types/debug-log-type';
import { SocialProvider } from '../../providers/social/social';

export type DebugDetailTab = 'request' | 'headers' | 'response' | 'details';

const REFRESH_INTERVAL = 1000;
const PAGE_SIZE = 200;
const SEARCH_DEBOUNCE = 250;
const FEEDBACK_DURATION = 1500;
const BACK_BUTTON_PRIORITY = 1000;

@Component({
  selector: 'rmt-debug-console',
  templateUrl: 'rmt-debug-console.html'
})
export class RmtDebugConsoleComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('panel') panel: ElementRef;
  @ViewChild('list') list: ElementRef;
  @Input() version: string;
  @Input() appInfo: string;
  @Output() close = new EventEmitter<void>();

  categories = DEBUG_LOG_CATEGORIES;
  httpTabs: DebugDetailTab[] = ['request', 'headers', 'response'];
  selected: DebugLogCategory[] = [];
  query = '';
  paused = false;
  newCount = 0;
  limit = PAGE_SIZE;
  total = 0;
  counts: { [category: string]: number } = {};
  visible: IDebugLogItem[] = [];
  filteredCount = 0;
  expanded: { [seqNr: number]: DebugDetailTab } = {};
  feedback: string = null;
  feedbackFailed = false;
  showAppInfo = false;
  isCordova = false;

  private items: IDebugLogItem[] = [];
  private searchTexts: { [seqNr: number]: string } = {};
  private tabTexts: { [key: string]: string } = {};
  private filtered: IDebugLogItem[] = [];
  private headSeqNr = 0;
  private clearedSeqNr = 0;
  private refreshTimer;
  private searchTimer;
  private feedbackTimer;
  private unregisterBackButton: Function;

  constructor(private platform: Platform,
              private zone: NgZone,
              private clipboard: Clipboard,
              private socialProvider: SocialProvider) {
  }

  ngOnInit() {
    this.isCordova = this.platform.is('cordova');
    this.reload();
    this.unregisterBackButton = this.platform.registerBackButtonAction(() => {
      this.zone.run(() => this.close.emit());
    }, BACK_BUTTON_PRIORITY);
    this.zone.runOutsideAngular(() => {
      this.refreshTimer = window.setInterval(() => this.refresh(), REFRESH_INTERVAL);
    });
  }

  ngAfterViewInit() {
    document.body.appendChild(this.panel.nativeElement);
  }

  ngOnDestroy() {
    const panel: HTMLElement = this.panel && this.panel.nativeElement;
    if (panel && panel.parentNode)
      panel.parentNode.removeChild(panel);
    window.clearInterval(this.refreshTimer);
    window.clearTimeout(this.searchTimer);
    window.clearTimeout(this.feedbackTimer);
    if (this.unregisterBackButton)
      this.unregisterBackButton();
  }

  refresh() {
    if (RmtDebugConsoleComponent.latestSeqNr() === this.headSeqNr)
      return;
    this.zone.run(() => {
      if (this.paused)
        this.newCount = this.countNewerThan(this.headSeqNr);
      else
        this.reload();
    });
  }

  reload() {
    this.headSeqNr = RmtDebugConsoleComponent.latestSeqNr();
    this.newCount = 0;
    const entries = LoggerProvider.debugEntries.filter((entry) => !!entry && entry.seqNr > this.clearedSeqNr);
    this.items = DebugLogViewMapper.toItems(entries);
    const searchTexts: { [seqNr: number]: string } = {};
    this.items.forEach((item) => {
      if (this.searchTexts[item.seqNr] !== undefined)
        searchTexts[item.seqNr] = this.searchTexts[item.seqNr];
    });
    this.searchTexts = searchTexts;
    this.applyFilter();
  }

  applyFilter() {
    const searched = this.query ? this.items.filter((item) => DebugLogViewMapper.matches(item, [], this.query, this.searchTextOf(item))) : this.items;
    const counts: { [category: string]: number } = {};
    searched.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    this.counts = counts;
    this.total = searched.length;
    this.filtered = this.selected.length ? searched.filter((item) => this.selected.indexOf(item.category) >= 0) : searched;
    this.filteredCount = this.filtered.length;
    this.visible = this.filtered.slice(0, this.limit);
  }

  selectAll() {
    this.selected = [];
    this.limit = PAGE_SIZE;
    this.applyFilter();
  }

  toggleCategory(category: DebugLogCategory) {
    this.selected = this.isSelected(category) ? this.selected.filter((c) => c !== category) : this.selected.concat(category);
    this.limit = PAGE_SIZE;
    this.applyFilter();
  }

  isSelected(category: DebugLogCategory): boolean {
    return this.selected.indexOf(category) >= 0;
  }

  onSearch(value: string) {
    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.query = (value || '').trim();
      this.limit = PAGE_SIZE;
      this.applyFilter();
    }, SEARCH_DEBOUNCE);
  }

  togglePause() {
    if (this.paused)
      this.resume();
    else
      this.paused = true;
  }

  resume() {
    this.paused = false;
    this.reload();
    if (this.list && this.list.nativeElement)
      this.list.nativeElement.scrollTop = 0;
  }

  clear() {
    this.clearedSeqNr = RmtDebugConsoleComponent.latestSeqNr();
    this.expanded = {};
    this.tabTexts = {};
    this.reload();
  }

  loadOlder() {
    this.limit += PAGE_SIZE;
    this.visible = this.filtered.slice(0, this.limit);
  }

  toggle(item: IDebugLogItem) {
    if (this.expanded[item.seqNr]) {
      delete this.expanded[item.seqNr];
      return;
    }
    this.expanded[item.seqNr] = this.defaultTab(item);
  }

  selectTab(item: IDebugLogItem, tab: DebugDetailTab) {
    this.expanded[item.seqNr] = tab;
  }

  defaultTab(item: IDebugLogItem): DebugDetailTab {
    if (!item.isHttp)
      return 'details';
    return item.entry === item.request ? 'request' : 'response';
  }

  tabLabel(tab: DebugDetailTab): string {
    return tab.charAt(0).toUpperCase() + tab.substr(1);
  }

  tabContent(item: IDebugLogItem, tab: DebugDetailTab): string {
    const key = item.seqNr + ':' + tab + ':' + (item.response ? item.response.seqNr : '');
    if (this.tabTexts[key] === undefined)
      this.tabTexts[key] = this.tabText(item, tab, DebugLogViewMapper.MAX_PRETTY);
    return this.tabTexts[key];
  }

  tabText(item: IDebugLogItem, tab: DebugDetailTab, maxLength = Infinity): string {
    if (tab === 'request')
      return DebugLogViewMapper.pretty(item.request.details, maxLength) || '(no body)';
    if (tab === 'response')
      return this.responseText(item, maxLength);
    if (tab === 'headers')
      return this.headersText(item);
    const message = item.entry.message && item.entry.message !== item.title ? item.entry.message + '\n\n' : '';
    return (message + DebugLogViewMapper.pretty(item.entry.details, maxLength)) || item.title;
  }

  private responseText(item: IDebugLogItem, maxLength: number): string {
    if (!item.response)
      return 'Waiting for response…';
    return item.response.message + '\n\n' + (DebugLogViewMapper.pretty(item.response.details, maxLength) || '(no body)');
  }

  private headersText(item: IDebugLogItem): string {
    const request = DebugLogViewMapper.formatHeaders(item.request.debugHeaders) || '(not captured)';
    let response = '(no response yet)';
    if (item.response)
      response = DebugLogViewMapper.formatHeaders(DebugLogViewMapper.responseHeaders(item.response)) || '(not captured)';
    return 'REQUEST HEADERS\n' + request + '\n\nRESPONSE HEADERS\n' + response;
  }

  badge(item: IDebugLogItem): string {
    const info = this.categories.filter((c) => c.category === item.category)[0];
    return info ? info.badge : '';
  }

  rowClass(item: IDebugLogItem): string {
    let css = 'dc-' + item.category;
    if (item.isWarning && item.category !== DebugLogCategory.Error)
      css += ' dc-warning';
    if (this.expanded[item.seqNr])
      css += ' dc-expanded';
    return css;
  }

  statusClass(status: number): string {
    if (status >= 200 && status < 300)
      return 'dc-status-ok';
    if (status >= 300 && status < 400)
      return 'dc-status-redirect';
    return 'dc-status-error';
  }

  trackBySeqNr(index: number, item: IDebugLogItem): number {
    return item.seqNr;
  }

  async copyTab(item: IDebugLogItem) {
    await this.copy(this.tabText(item, this.expanded[item.seqNr]), 'Copied');
  }

  async copyCurl(item: IDebugLogItem) {
    await this.copy(DebugLogViewMapper.toCurl(item), 'cURL copied');
  }

  async copyAll() {
    await this.copy(LoggerProvider.getDebugText(this.filteredEntries()), 'Copied ' + this.filteredCount + (this.filteredCount === 1 ? ' entry' : ' entries'));
  }

  async share() {
    await this.socialProvider.share(LoggerProvider.getDebugText(this.filteredEntries()));
  }

  async copy(text: string, message: string) {
    if (!text)
      return;
    try {
      if (this.isCordova)
        await this.clipboard.copy(text);
      else
        await RmtDebugConsoleComponent.copyInBrowser(text);
      this.showFeedback(message);
    } catch (ex) {
      this.showFeedback('Copy failed', true);
    }
  }

  private showFeedback(message: string, failed = false) {
    this.feedback = message;
    this.feedbackFailed = failed;
    window.clearTimeout(this.feedbackTimer);
    this.feedbackTimer = window.setTimeout(() => {
      this.feedback = null;
    }, FEEDBACK_DURATION);
  }

  private searchTextOf(item: IDebugLogItem): string {
    if (this.searchTexts[item.seqNr] === undefined)
      this.searchTexts[item.seqNr] = DebugLogViewMapper.searchText(item);
    return this.searchTexts[item.seqNr];
  }

  private filteredEntries(): LoggerEntry[] {
    return this.filtered.map((item) => item.entry);
  }

  private countNewerThan(seqNr: number): number {
    let count = 0;
    const entries = LoggerProvider.debugEntries;
    while (count < entries.length && entries[count] && entries[count].seqNr > seqNr)
      count++;
    return count;
  }

  private static latestSeqNr(): number {
    const head = LoggerProvider.debugEntries[0];
    return head ? head.seqNr : 0;
  }

  private static async copyInBrowser(text: string) {
    const clipboard = (navigator as any).clipboard;
    try {
      await clipboard.writeText(text);
    } catch (ex) {
      RmtDebugConsoleComponent.copyWithSelection(text);
    }
  }

  private static copyWithSelection(text: string) {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    if (!copied)
      throw new Error('Copy failed');
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-debug-console/rmt-debug-console.ts